import { memo, useCallback, useMemo } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { TransitionInEffect } from '@goldcast/player-config/src/types/transitions';
import PlayerMenuTooltip from '../../../PlayerMenus/PlayerMenuTooltip';
import { getTextElementById } from '../utils';
import { updateTextElementProperties } from './textElementPopoverUtils';
import { classnames } from '@/libs/utils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import Icon from '@/components/atoms/Icon';

interface TextElementAnimationPopoverProps {
  textElementId: string;
}

enum AnimationMode {
  PerWord = 'Per Word',
  PerSlide = 'Per Slide'
}

const MODE_OPTIONS = [
  { label: 'Per Word', value: AnimationMode.PerWord },
  { label: 'Per Slide', value: AnimationMode.PerSlide }
];

const EFFECT_OPTIONS = [
  { label: 'None', value: TransitionInEffect.None },
  { label: 'Fade In', value: TransitionInEffect.Fade },
  { label: 'Slide Up', value: TransitionInEffect.SlideUp },
  { label: 'Spring', value: TransitionInEffect.Spring }
];

function TextElementAnimationPopover({ textElementId }: TextElementAnimationPopoverProps) {
  const { clipId, clipData } = useClipsContext();

  const selectedElement = useMemo(() => {
    return getTextElementById(clipData, textElementId);
  }, [clipData, textElementId]);

  const animationMode = useMemo(() => {
    const wordIn = selectedElement?.transitions?.wordIn;
    return wordIn ? AnimationMode.PerWord : AnimationMode.PerSlide;
  }, [selectedElement?.transitions?.wordIn]);

  const animationEffect = useMemo(() => {
    if (animationMode === AnimationMode.PerWord) {
      return selectedElement?.transitions?.wordIn || TransitionInEffect.None;
    }

    if (animationMode === AnimationMode.PerSlide) {
      return selectedElement?.transitions?.pageIn || TransitionInEffect.None;
    }

    return TransitionInEffect.None;
  }, [animationMode, selectedElement?.transitions?.pageIn, selectedElement?.transitions?.wordIn]);

  const onAnimationModeChange = useCallback(
    (mode: AnimationMode) => {
      const effectToUse = animationEffect;
      updateTextElementProperties(clipId, clipData, textElementId, {
        transitions: {
          wordIn: mode === AnimationMode.PerWord ? effectToUse : undefined,
          pageIn: mode === AnimationMode.PerSlide ? effectToUse : undefined
        }
      });
    },
    [clipId, clipData, textElementId, animationEffect]
  );

  const onAnimationEffectChange = useCallback(
    (newEffect: TransitionInEffect) => {
      const effectToUse = newEffect === TransitionInEffect.None ? undefined : newEffect;
      updateTextElementProperties(clipId, clipData, textElementId, {
        transitions: {
          wordIn: animationMode === AnimationMode.PerWord ? effectToUse : undefined,
          pageIn: animationMode === AnimationMode.PerSlide ? effectToUse : undefined
        }
      });
    },
    [clipId, clipData, textElementId, animationMode]
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="text-element-animation-button"
            className={classnames(
              'group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100',
              open ? 'bg-slate-100' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <Icon name="IconBounceRight" size={16} />
              <PlayerMenuTooltip content="Animation Mode" />
            </div>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="absolute z-50 mt-1 w-64 translate-y-1 space-y-5">
              <div className="space-y-4 rounded-lg border bg-white p-4 shadow-lg">
                <h3 className="text-sm font-medium text-slate-500">Animation Settings</h3>

                <div className="grid grid-cols-12 items-center justify-between gap-2">
                  <span className="col-span-3 text-sm font-medium">Mode</span>
                  <span className="col-span-9 flex items-center">
                    <select
                      className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                      value={animationMode}
                      onChange={e => onAnimationModeChange(e.target.value as AnimationMode)}
                    >
                      {MODE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>

                <div className="grid grid-cols-12 items-center justify-between gap-2">
                  <span className="col-span-3 text-sm font-medium">Effect</span>
                  <span className="col-span-9 flex items-center">
                    <select
                      className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                      value={animationEffect}
                      onChange={e => onAnimationEffectChange(e.target.value as TransitionInEffect)}
                    >
                      {EFFECT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(TextElementAnimationPopover);
